import * as React from "react";
import { AlertTriangle, CheckCircle2, GitBranch, Loader2, MessageSquareWarning, X, XCircle } from "lucide-react";
import { useEpicWrapUp } from "@/hooks/queries/use-epics";
import {
  wrapUpChecks,
  wrapUpFindings,
  type EpicDisplayState,
  type EpicWrapUpView,
  type WrapUpCheck,
  type WrapUpCheckClassification,
} from "@/lib/api/epics";
import { cn } from "@/lib/utils";

/** Display states that get a banner. "merged" and "none" show nothing. */
type BannerState = Exclude<EpicDisplayState, "merged" | "none">;

const BANNER: Record<
  BannerState,
  { title: string; nextSteps: string; className: string; icon: typeof AlertTriangle; role: "status" | "alert" }
> = {
  running: {
    title: "Wrapping up the build",
    nextSteps: "CompBox is running the project checks on the build. Nothing to do yet; this updates when it finishes.",
    className: "border-blue-500/30 bg-blue-500/10",
    icon: Loader2,
    role: "status",
  },
  ready: {
    title: "Ready for you",
    nextSteps: "Every check passed. Review the pull request and merge it when you're happy.",
    className: "border-green-500/30 bg-green-500/10",
    icon: CheckCircle2,
    role: "status",
  },
  needs_decision: {
    title: "The build needs a decision",
    nextSteps:
      "Read the findings below. Report a gap to queue a follow-up build, or merge as is if they don't matter. The pull request stays a draft until you decide.",
    className: "border-amber-500/30 bg-amber-500/10",
    icon: AlertTriangle,
    role: "alert",
  },
  failed: {
    title: "Wrap-up failed",
    nextSteps:
      "Fix what failed: report a gap to queue a follow-up build, or push a fix to the branch. The pull request stays a draft until the checks pass.",
    className: "border-red-500/30 bg-red-500/10",
    icon: XCircle,
    role: "alert",
  },
  follow_up: {
    title: "Follow-up build in progress",
    nextSteps: "A follow-up build is working on this epic. The wrap-up runs again when it finishes.",
    className: "border-purple-500/30 bg-purple-500/10",
    icon: GitBranch,
    role: "status",
  },
};

function isBannerState(state: EpicDisplayState): state is BannerState {
  return state !== "merged" && state !== "none";
}

/** Storage key for a dismissed banner; one per epic. */
export function wrapUpDismissKey(epicId: string): string {
  return `compbox:wrap-up-banner-dismissed:${epicId}`;
}

/**
 * The value a dismissal records: the attempt plus the state, so a new attempt
 * (or the same attempt moving to another state) shows the banner again.
 */
export function wrapUpDismissValue(view: Pick<EpicWrapUpView, "wrapUp" | "displayState">): string {
  return `${String(view.wrapUp?.attempt ?? 0)}:${view.displayState}`;
}

function readDismissed(epicId: string): string | null {
  try {
    return window.localStorage.getItem(wrapUpDismissKey(epicId));
  } catch {
    return null;
  }
}

function writeDismissed(epicId: string, value: string): void {
  try {
    window.localStorage.setItem(wrapUpDismissKey(epicId), value);
  } catch {
    // Storage unavailable (private mode): the dismissal lasts until reload.
  }
}

/** The checks summary, with failures grouped by why they failed (ENG-396). */
export interface ChecksSummary {
  /** "3 of 4 checks passed". */
  summary: string;
  /** Failures this build caused (legacy failures without a classification land here). */
  caused: string[];
  /** Failures that happen on the base branch too. */
  base: string[];
  /** Failures that passed when run again. */
  flaky: string[];
}

/** "3 of 4 checks passed", plus the failures grouped as caused, already on the base, or flaky. */
export function summarizeChecks(view: Pick<EpicWrapUpView, "wrapUp">): ChecksSummary | null {
  const checks = wrapUpChecks(view.wrapUp);
  if (checks.length === 0) return null;
  const passed = checks.filter((c) => c.passed).length;
  const label = (c: WrapUpCheck) => (c.timedOut ? `${c.name} (timed out)` : c.name);
  const failedIn = (group: WrapUpCheckClassification) =>
    checks.filter((c) => !c.passed && (c.classification ?? "caused") === group).map(label);
  const summary =
    passed === checks.length
      ? `All ${String(checks.length)} ${checks.length === 1 ? "check" : "checks"} passed`
      : `${String(passed)} of ${String(checks.length)} checks passed`;
  return { summary, caused: failedIn("caused"), base: failedIn("base"), flaky: failedIn("flaky") };
}

const CHECK_GROUPS: { key: "caused" | "base" | "flaky"; label: string }[] = [
  { key: "caused", label: "Caused by this build" },
  { key: "base", label: "Already failing on the base branch" },
  { key: "flaky", label: "Flaky (passed when run again)" },
];

/** A feature the failed wrap-up may point at (ENG-355). */
export interface WrapUpGapFeature {
  id: string;
  identifier?: string | null;
}

/** What the "Report a gap" button hands to the report-gap dialog (ENG-355). */
export interface WrapUpGapReport {
  /** The one feature the findings name, or null when none or several do. */
  featureId: string | null;
  /** A short bullet list of what failed. */
  summary: string;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Works out what to pre-fill the report-gap dialog with from a failed wrap-up:
 * the feature its findings name (only when exactly one feature is named) and a
 * bullet list of the findings, falling back to the failed checks. Exported for tests.
 */
export function wrapUpGapReport(
  view: Pick<EpicWrapUpView, "wrapUp">,
  features: readonly WrapUpGapFeature[],
): WrapUpGapReport {
  const findings = wrapUpFindings(view.wrapUp);
  const checks = summarizeChecks(view);
  const failedChecks = checks ? [...checks.caused, ...checks.base, ...checks.flaky] : [];
  const lines = findings.length > 0 ? findings : failedChecks.map((name) => `The ${name} check failed.`);
  const summary = lines.map((line) => `- ${line}`).join("\n");

  const named = features.filter((f) =>
    findings.some(
      (finding) =>
        finding.includes(f.id) ||
        (!!f.identifier && new RegExp(`(^|[^\\w-])${escapeRegExp(f.identifier)}(?![\\w-])`).test(finding)),
    ),
  );
  return { featureId: named.length === 1 ? named[0]?.id ?? null : null, summary };
}

/** The banner for a loaded wrap-up view. Exported for tests. */
export function EpicWrapUpBannerView({
  epicId,
  view,
  features = [],
  onReportGap,
}: {
  epicId: string;
  view: EpicWrapUpView;
  /** The epic's features, used to work out which one the failure is about (ENG-355). */
  features?: readonly WrapUpGapFeature[];
  /** ENG-355: when set, a failed wrap-up offers "Report a gap", which calls this. */
  onReportGap?: (report: WrapUpGapReport) => void;
}) {
  const dismissValue = wrapUpDismissValue(view);
  const [dismissed, setDismissed] = React.useState<string | null>(() => readDismissed(epicId));

  React.useEffect(() => {
    setDismissed(readDismissed(epicId));
  }, [epicId]);

  const state = view.displayState;
  if (!isBannerState(state) || dismissed === dismissValue) return null;

  const config = BANNER[state];
  const Icon = config.icon;
  const checks = state === "follow_up" ? null : summarizeChecks(view);
  const findings = state === "needs_decision" || state === "failed" ? wrapUpFindings(view.wrapUp) : [];

  const dismiss = () => {
    writeDismissed(epicId, dismissValue);
    setDismissed(dismissValue);
  };

  return (
    <div
      role={config.role}
      data-testid="epic-wrap-up-banner"
      data-state={state}
      className={cn("flex items-start gap-3 border-b px-4 md:px-6 py-3", config.className)}
    >
      <Icon className={cn("h-5 w-5 mt-0.5 shrink-0", state === "running" && "animate-spin")} aria-hidden />
      <div className="flex-1 min-w-0 space-y-1 text-sm">
        <p className="font-semibold">
          {config.title}
          {view.wrapUp && state !== "follow_up" && (
            <span className="ml-2 font-normal text-muted-foreground">Attempt {view.wrapUp.attempt}</span>
          )}
        </p>
        {checks && <p data-testid="wrap-up-checks">{checks.summary}</p>}
        {checks && CHECK_GROUPS.some((g) => checks[g.key].length > 0) && (
          <ul className="space-y-0.5" data-testid="wrap-up-check-groups">
            {CHECK_GROUPS.filter((g) => checks[g.key].length > 0).map((g) => (
              <li key={g.key} data-testid={`wrap-up-checks-${g.key}`}>
                <span className="font-medium">{g.label}:</span> {checks[g.key].join(", ")}
              </li>
            ))}
          </ul>
        )}
        {findings.length > 0 && (
          <ul className="list-disc pl-5 space-y-0.5" data-testid="wrap-up-findings">
            {findings.map((finding, i) => (
              <li key={i}>{finding}</li>
            ))}
          </ul>
        )}
        <p className="text-muted-foreground">{config.nextSteps}</p>
        {state === "failed" && onReportGap && (
          <button
            type="button"
            data-testid="wrap-up-report-gap"
            onClick={() => {
              onReportGap(wrapUpGapReport(view, features));
            }}
            className="mt-1 inline-flex items-center gap-1.5 rounded border border-current/30 px-2 py-1 text-xs font-medium hover:bg-white/5"
          >
            <MessageSquareWarning className="h-3.5 w-3.5" aria-hidden />
            Report a gap
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss wrap-up banner"
        className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * Where the epic's build wrap-up stands (ENG-329): running, ready, needs a
 * decision, failed, or a follow-up build in progress. Dismissing hides it for
 * the current attempt only; the next attempt shows it again.
 */
export function EpicWrapUpBanner({ epicId }: { epicId: string }) {
  const { data } = useEpicWrapUp(epicId);
  if (!data) return null;
  return <EpicWrapUpBannerView epicId={epicId} view={data} />;
}
